import { useSiteContext, useOrganizationId } from 'application/contexts/SiteContext';
import { personToJsonLd, addressToJsonLd, resolveLogoUrl, deriveAreaServed } from 'helpers/jsonLd';
import { JsonLdScript } from './JsonLdScript';

export const OrganizationJsonLd: React.FC = () => {
	const site = useSiteContext();
	const organizationId = useOrganizationId();
	const org = site?.organization;

	if (!site?.title && !org?.legalName) return null;

	const logoUrl = resolveLogoUrl(site?.url, site?.settings?.globalTheme?.setTheme);

	const founders = (org?.founders ?? []).map((founder) => personToJsonLd(founder, organizationId)).filter(Boolean);
	const addresses = (org?.addresses ?? []).map(addressToJsonLd);

	const organization = {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		'@id': organizationId,
		name: site?.title || org?.legalName,
		legalName: org?.legalName || undefined,
		description: org?.description || undefined,
		url: site?.url || undefined,
		logo: logoUrl ? { '@type': 'ImageObject', url: logoUrl } : undefined,
		foundingDate: org?.foundingDate || undefined,
		founder: founders.length ? founders : undefined,
		knowsAbout: org?.knowsAbout?.length ? org.knowsAbout : undefined,
		sameAs: org?.sameAs?.length ? org.sameAs : undefined,
		address: addresses.length > 1 ? addresses : addresses[0] || undefined,
		contactPoint:
			org?.contactEmail || org?.contactPhone
				? {
						'@type': 'ContactPoint',
						email: org?.contactEmail || undefined,
						telephone: org?.contactPhone || undefined,
						areaServed: deriveAreaServed(site?.culture),
				  }
				: undefined,
	};

	return (
		<>
			<JsonLdScript id="organization-jsonld" data={organization} />
			{site?.url && (
				<JsonLdScript
					id="website-jsonld"
					data={{
						'@context': 'https://schema.org',
						'@type': 'WebSite',
						'@id': `${site.url}#website`,
						name: site?.title,
						url: site.url,
						inLanguage: site?.culture || undefined,
						publisher: organizationId ? { '@id': organizationId } : undefined,
					}}
				/>
			)}
		</>
	);
};
